import React, { useCallback, useEffect, useState } from "react";
import { useActiveIModelConnection } from "@itwin/appui-react";
import { useAccessToken } from "@itwin/web-viewer-react";
import { getBackendBase } from "../trimble/coreFiles";
import {
  queryNiceConnectAspectsForElement,
  type NiceConnectAspectRow,
} from "../services/niceConnectAspect";
const NICECONNECT_ASPECT_CLASS =
  "DgnCustomItemTypes_DOT_DigitalAssetMetadata.NiceConnectElementAspect";

function getSelectedElementIds(selectionSet: any): string[] {
  const activeElements = selectionSet?.active?.elements;
  if (activeElements && typeof activeElements[Symbol.iterator] === "function") {
    return Array.from(activeElements, (id) => String(id ?? "").trim()).filter(Boolean);
  }

  const elements = selectionSet?.elements;
  if (elements && typeof elements[Symbol.iterator] === "function") {
    return Array.from(elements, (id) => String(id ?? "").trim()).filter(Boolean);
  }

  return [];
}

function formatAspectPropertyName(name: string): string {
  return String(name ?? "")
    .replace(/^ECInstanceId$/i, "Aspect Id")
    .replace(/^Element$/i, "Element")
    .replace(/_x([0-9a-fA-F]{4})_/g, (_match, hex) =>
      String.fromCharCode(Number.parseInt(hex, 16))
    )
    .replace(/_/g, " ")
    .trim();
}

function formatAspectValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export const NiceConnectAspectWidget: React.FC = () => {
  const iModelConnection = useActiveIModelConnection();
  const accessToken = useAccessToken();
  const [selectedElementIds, setSelectedElementIds] = useState<string[]>([]);
  const [aspectRows, setAspectRows] = useState<NiceConnectAspectRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshSelection = useCallback(async () => {
    if (!iModelConnection) {
      setSelectedElementIds([]);
      setAspectRows([]);
      setError(null);
      return;
    }

    const ids = getSelectedElementIds((iModelConnection as any).selectionSet);
    setSelectedElementIds(ids);

    if (ids.length === 0) {
      setAspectRows([]);
      setError(null);
      return;
    }

    const backendBase = getBackendBase();
    if (!backendBase) {
      setAspectRows([]);
      setError("NiceConnect backend base is not configured.");
      return;
    }
    if (!accessToken) {
      setAspectRows([]);
      setError("Bentley access token is unavailable.");
      return;
    }

    setIsLoading(true);
    setError(null);
      try {
        const rows = await queryNiceConnectAspectsForElement({
          accessToken,
          backendBase,
          elementId: ids[0],
          iTwinId: String((iModelConnection as any).iTwinId ?? "").trim(),
          iModelId: String((iModelConnection as any).iModelId ?? "").trim(),
        });
        setAspectRows(rows);
      } catch (err) {
      setAspectRows([]);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, iModelConnection]);

  useEffect(() => {
    void refreshSelection();
    if (!iModelConnection) return;

    const removeListener = (iModelConnection as any).selectionSet.onChanged.addListener(() => {
      void refreshSelection();
    });

    return () => {
      removeListener?.();
    };
  }, [iModelConnection, refreshSelection]);

  return (
    <div style={{ padding: 12, fontSize: 13, lineHeight: 1.4 }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>NiceConnect Properties</div>
      {selectedElementIds.length === 0 && (
        <div>Select an element to inspect its NiceConnect item type.</div>
      )}
      {selectedElementIds.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div>Selected element: {selectedElementIds[0]}</div>
          {selectedElementIds.length > 1 && (
            <div>Additional selected elements: {selectedElementIds.length - 1}</div>
          )}
        </div>
      )}
      {isLoading && <div>Loading NiceConnect aspect...</div>}
      {error && (
        <div style={{ color: "#b42318", whiteSpace: "pre-wrap" }}>
          Failed to load NiceConnect aspect: {error}
        </div>
      )}
      {!isLoading && !error && selectedElementIds.length > 0 && aspectRows.length === 0 && (
        <div>No `NiceConnectElementAspect` assignment found on the selected element.</div>
      )}
      {!isLoading &&
        !error &&
        aspectRows.map((row, index) => (
          <div
            key={index}
            style={{
              marginTop: 10,
              paddingTop: 10,
              borderTop: "1px solid #d0d5dd",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{NICECONNECT_ASPECT_CLASS}</div>
            {Object.entries(row)
              .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "")
              .map(([name, value]) => (
                <div key={`${index}-${name}`} style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11, color: "#475467", textTransform: "uppercase" }}>
                    {formatAspectPropertyName(name)}
                  </div>
                  <div>{formatAspectValue(value)}</div>
                </div>
              ))}
          </div>
        ))}
    </div>
  );
};
